import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import PgIcon from './pg-icon';
import PgNavMenuItems from './pg-nav-menu-items';
import PgNavBottom from './pg-nav-bottom';
import Image from 'next/image';

const navItemShape = PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.node.isRequired,
    icon: PropTypes.string,
    href: PropTypes.string,
    hasSubmenu: PropTypes.bool,
    onClick: PropTypes.func,
    actionIcons: PropTypes.array,
});

const autoDetectActiveId = (topNav, bottomNav, path) => {
    const allItems = [...topNav, ...bottomNav];
    const match = allItems.find((item) => item.href && item.href === path);
    return match ? match.id : undefined;
};

/**
 * The sidebar's base layout: logo + collapse toggle, a fixed top nav, a
 * middle zone left open for whatever the page wants there, and a bottom
 * zone (`PgNavBottom`) with the user/business dropdown. Deliberately thin
 * — everything that can stand on its own lives in its own `pg-*.js` file;
 * see each piece's section in pegment.md for its own docs.
 */
const PgSidebar = forwardRef(({
    logoWord,
    topNav,
    bottomNav,
    children,
    user,
    businesses,
    active,
    defaultCollapsed,
    settingsHref,
    onNavClick,
    onCollapsedChange,
    onBusinessChange,
    onDropdownAction,
    onHelpAction,
    className,
}, ref) => {
    const router = useRouter();
    const rootRef = useRef(null);

    const [collapsed, setCollapsed] = useState(defaultCollapsed);

    const toggle = useCallback(() => setCollapsed((c) => {
        const next = !c;
        onCollapsedChange?.(next);
        return next;
    }), [onCollapsedChange]);
    const collapse = useCallback(() => setCollapsed(true), []);
    const expand = useCallback(() => setCollapsed(false), []);

    useImperativeHandle(ref, () => ({ toggle, collapse, expand }), [toggle, collapse, expand]);

    const activeId = active ?? autoDetectActiveId(topNav, bottomNav, router.asPath);

    const [colorScheme, setColorScheme] = useState(() =>
        typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    );

    React.useEffect(() => {
        const media = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = (e) => setColorScheme(e.matches ? 'dark' : 'light');
        media.addEventListener('change', handler);
        return () => media.removeEventListener('change', handler);
    }, []);
    const defaultLogoUrl = `https://cdn.workpeg.com/pegs/home/assets/logo/primary-${colorScheme}.svg`;

    const logoUrl =`https://cdn.workpeg.com/pegs/home/assets/logo/primary-${colorScheme}.svg`;


    const sidebarClasses = ['pg-sidebar', collapsed && 'is-collapsed', className].filter(Boolean).join(' ');

    return (
        <div ref={rootRef} style={{ display: 'block', flexShrink: 0 }}>
            <div className={sidebarClasses}>
                <div className="pg-sidebar-top">
                    <div className="pg-sidebar-header">
                        {!collapsed && <Image className='' width={92} height={15} src={logoUrl} alt={logoWord}
                        onError={(e) => {
                            e.target.onerror = null; // Prevent looping
                            e.target.src = defaultLogoUrl;
                        }}
                        />}
                        <button type="button" className="pg-sidebar-toggle" title="Toggle sidebar" onClick={toggle}>
                            <PgIcon name="layout-sidebar" size={20} />
                        </button>
                    </div>
                    <PgNavMenuItems items={topNav} activeId={activeId} onItemClick={onNavClick} />
                </div>

                <div className="pg-sidebar-mid">
                    {children}
                </div>

                <div className="pg-sidebar-divider" />
                <PgNavBottom
                    user={user}
                    businesses={businesses}
                    settingsHref={settingsHref}
                    onBusinessChange={onBusinessChange}
                    onDropdownAction={onDropdownAction}
                    onHelpAction={onHelpAction}
                    sidebarRef={rootRef}
                />
            </div>
        </div>
    );
});
PgSidebar.displayName = 'PgSidebar';

PgSidebar.propTypes = {
    logoMark: PropTypes.string,
    logoWord: PropTypes.string,
    logoSrcLight: PropTypes.string,
    logoSrcDark: PropTypes.string,
    logoMarkSrcLight: PropTypes.string,
    logoMarkSrcDark: PropTypes.string,
    /** Fixed items pinned below the logo, e.g. Search / Workpeg AI / Dashboard (or "Get started" while onboarding). Rendered via `PgNavMenuItems`. */
    topNav: PropTypes.arrayOf(navItemShape),
    /** Static rows pinned above the user row in the bottom zone, e.g. Settings / Report a Bug / Help & Support. Forwarded to `PgNavBottom`'s `items`. */
    bottomNav: PropTypes.arrayOf(navItemShape),
    /** Middle-zone content — put whatever nav/content the page needs here, typically your own `PgNavMenuItems`. Unlike `topNav`/`bottomNav`, this is a free slot: `PgSidebar` doesn't compute active state or wire up clicks for it. */
    children: PropTypes.node,
    /** Bottom user row. `avatar` (direct image URL) takes priority over `email`'s Gravatar lookup. */
    user: PropTypes.shape({
        name: PropTypes.string,
        email: PropTypes.string,
        avatar: PropTypes.string,
        company: PropTypes.string,
    }),
    /** `[{ id, name, plan, active }]` — rendered by `PgNavBottom` in the user-row dropdown, scrollable past 3. */
    businesses: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string,
        plan: PropTypes.string,
        active: PropTypes.bool,
    })),
    /** Controlled active nav item id, matched against `topNav`/`bottomNav`. Omit to auto-detect from the current route against each item's `href`. */
    active: PropTypes.string,
    defaultCollapsed: PropTypes.bool,
    /** When set, "Settings" in the dropdown renders as a `next/link` to this path instead of firing `onDropdownAction('settings')`. */
    settingsHref: PropTypes.string,
    /** Called with an item's `id` for both `topNav` and `bottomNav` clicks, after that item's own `onClick` (if any) runs. */
    onNavClick: PropTypes.func,
    onCollapsedChange: PropTypes.func,
    onBusinessChange: PropTypes.func,
    /** Fired for `'add'`, `'settings'` (only when `settingsHref` is unset) and `'logout'`. */
    onDropdownAction: PropTypes.func,
    /** Fired for `'help-center'` or `'report-bug'`. */
    onHelpAction: PropTypes.func,
    className: PropTypes.string,
};

PgSidebar.defaultProps = {
    logoMark: 'P',
    logoWord: 'PEGMENT',
    logoSrcLight: undefined,
    logoSrcDark: undefined,
    logoMarkSrcLight: undefined,
    logoMarkSrcDark: undefined,
    topNav: [],
    bottomNav: [],
    children: null,
    user: {},
    businesses: [],
    active: undefined,
    defaultCollapsed: false,
    settingsHref: undefined,
    onNavClick: undefined,
    onCollapsedChange: undefined,
    onBusinessChange: undefined,
    onDropdownAction: undefined,
    onHelpAction: undefined,
    className: '',
};

export default PgSidebar;
